/**
 * ナレーション中に BGM を自動で下げる「ダッキング」の純関数
 * (docs/features/video-editor-capability-gaps-plan.md §4.3-E)。
 *
 * サイドチェイン (実際の音量を解析して追従する) ではなく、**ナレーション区間から決定的に計算した
 * 音量エンベロープ**を再生時に掛ける方式。プレビュー・MP4 レンダラー・Premiere の Audio Levels
 * キーフレームが同じ式を使えるよう、ここには zod も React も持ち込まない (Node の単体テストで叩ける)。
 *
 * 対象は **BGM トラック (bgmTrack) だけ**。効果音 (SE) は「決めどころで鳴らす」ものなので下げず、
 * シーン音声は従来どおり AI の「音量バランス」複合編集 (item.volume) で扱う。
 *
 * 正本はこのファイルで、`video-editor/src/lib/ducking.ts` (レンダラー) と
 * `backend/services/video_edit/ducking.py` (BE) が手動コピー。3 つに同じケースのテストを置いて
 * 一致を担保する。
 */

/** ダッキングの設定 (プロジェクト単位)。保存形は project.ducking。 */
export interface DuckingSettings {
  /** false = 掛けない (既定)。既存プロジェクトの音を変えないため既定は無効。 */
  enabled: boolean;
  /** ナレーション中の BGM の音量 (dB。0 = 下げない・-24 が下限)。 */
  targetDb: number;
  /** 下がりきるまでの時間 (秒)。区間の開始の attackSec 前から下がり始める。 */
  attackSec: number;
  /** 戻りきるまでの時間 (秒)。区間の終わりから releaseSec で 1.0 に戻る。 */
  releaseSec: number;
  /** この間隔以下のナレーションの切れ目は繋げて下げたままにする (秒)。 */
  holdSec: number;
}

/** 外部 (保存 JSON / AI) からの入力。キーは欠けていてよい。 */
export interface DuckingSettingsInput {
  enabled?: unknown;
  targetDb?: unknown;
  attackSec?: unknown;
  releaseSec?: unknown;
  holdSec?: unknown;
}

/** 下げる区間 (絶対秒の半開区間)。ナレーション区間を holdSec で結合したもの。 */
export interface DuckingInterval {
  startSec: number;
  endSec: number;
}

export const DUCKING_TARGET_DB_MIN = -24;
export const DUCKING_TARGET_DB_MAX = 0;
export const DUCKING_TARGET_DB_STEP = 0.5;
export const DUCKING_ATTACK_SEC_MIN = 0.05;
export const DUCKING_ATTACK_SEC_MAX = 1;
export const DUCKING_RELEASE_SEC_MIN = 0.1;
export const DUCKING_RELEASE_SEC_MAX = 2;
export const DUCKING_HOLD_SEC_MIN = 0;
export const DUCKING_HOLD_SEC_MAX = 2;

/** 既定値。`enabled: false` なので、設定を持たない既存プロジェクトの音は一切変わらない。 */
export const DUCKING_DEFAULTS: DuckingSettings = {
  enabled: false,
  targetDb: -10,
  attackSec: 0.25,
  releaseSec: 0.6,
  holdSec: 0.5,
};

/**
 * UI の「強さ」セレクタの選択肢 (§4.3-E の最小 UI)。attack / release / hold は AI 専用なので
 * UI からは触れない。値は 3 実装で同値。
 */
export const DUCKING_STRENGTH_PRESETS: { targetDb: number; label: string }[] = [
  { targetDb: -6, label: '弱' },
  { targetDb: -10, label: '標準' },
  { targetDb: -16, label: '強' },
];

const EPS = 1e-9;

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/** 半端な浮動小数を作らないための half-up の丸め (拡張属性の丸めと同じ規則)。 */
function roundToStep(value: number, step: number): number {
  const inv = 1 / step;
  return Math.floor(value * inv + 0.5) / inv;
}

function clampNumber(value: unknown, min: number, max: number, fallback: number, step: number): number {
  if (!isFiniteNumber(value)) return fallback;
  return roundToStep(Math.min(max, Math.max(min, value)), step);
}

/**
 * 保存形 / AI の指定を正規化する。`null` / オブジェクトでない値は `undefined` (設定なし)。
 * 欠けたキーは既定値で埋めるので、返り値は必ず 5 キーすべてを持つ (保存値 = 実効値)。
 */
export function normalizeDucking(raw: unknown): DuckingSettings | undefined {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const input = raw as DuckingSettingsInput;
  return {
    enabled: input.enabled === true,
    targetDb: clampNumber(
      input.targetDb, DUCKING_TARGET_DB_MIN, DUCKING_TARGET_DB_MAX,
      DUCKING_DEFAULTS.targetDb, DUCKING_TARGET_DB_STEP,
    ),
    attackSec: clampNumber(
      input.attackSec, DUCKING_ATTACK_SEC_MIN, DUCKING_ATTACK_SEC_MAX, DUCKING_DEFAULTS.attackSec, 0.01,
    ),
    releaseSec: clampNumber(
      input.releaseSec, DUCKING_RELEASE_SEC_MIN, DUCKING_RELEASE_SEC_MAX, DUCKING_DEFAULTS.releaseSec, 0.01,
    ),
    holdSec: clampNumber(
      input.holdSec, DUCKING_HOLD_SEC_MIN, DUCKING_HOLD_SEC_MAX, DUCKING_DEFAULTS.holdSec, 0.01,
    ),
  };
}

/** 実際に音が下がる設定か (無効・targetDb 0 では何もしない)。 */
export function isDuckingActive(raw: unknown): boolean {
  const ducking = normalizeDucking(raw);
  return Boolean(ducking && ducking.enabled && ducking.targetDb < 0);
}

/** targetDb → リニアのゲイン (10^(dB/20))。0 dB = 1.0。 */
export function duckingTargetGain(targetDb: number): number {
  return Math.pow(10, targetDb / 20);
}

// ── 区間の算出 ────────────────────────────────────────────────────────────────

/** 区間の算出に必要なナレーション cue の形 (構造的に宣言する)。 */
export interface DuckingNarrationCueLike {
  clipId: string;
  startSec: number;
  muted?: boolean;
  /** 出点 (音源の頭からの秒。途中で切るとき。narrationCuePlaySec を参照) */
  durationSec?: number;
  /** 入り点 (音源の頭からの秒。頭を切り詰めるとき。narrationCuePlaySec を参照) */
  sourceStartSec?: number;
}

/** ナレーション cue の入り点 (音源の頭からの秒)。未指定・不正値は 0 (頭から)。 */
export function narrationCueSourceStartSec(cue: { sourceStartSec?: number }): number {
  return isFiniteNumber(cue.sourceStartSec) && cue.sourceStartSec > 0 ? cue.sourceStartSec : 0;
}

/**
 * ナレーション cue 1 本が実際に鳴る長さ (秒) = 出点 - 入り点。
 * 出点は音源の実尺と cue の durationSec (音源の頭からの秒) の短い方 (片方しか分からなければその値)、
 * 入り点は sourceStartSec (省略 = 0)。出点がどちらも分からなければ null (終わりが決まらない)。
 * 入り点が出点以降なら 0 (鳴らない。保存時検証と UI が防ぐ)。
 * 再生 (EditVideo)・ダッキング区間・タイムライン表示・XML 書き出しが共通で使う
 * (backend ducking.narration_cue_play_sec / video-editor の ducking.ts と同じ式)。
 */
export function narrationCuePlaySec(
  cue: { durationSec?: number; sourceStartSec?: number },
  sourceDurationSec: number | null | undefined,
): number | null {
  const source = isFiniteNumber(sourceDurationSec) && sourceDurationSec > 0 ? sourceDurationSec : null;
  const trim = isFiniteNumber(cue.durationSec) && cue.durationSec > 0 ? cue.durationSec : null;
  const end = source === null ? trim : trim === null ? source : Math.min(source, trim);
  if (end === null) return null;
  return Math.max(0, end - narrationCueSourceStartSec(cue));
}

/** 区間の算出に必要なクリップの形。実尺 (narrationDurationSec) が無い cue は区間にしない。 */
export interface DuckingClipLike {
  id: string;
  narrationDurationSec?: number | null;
}

/** 区間の算出に必要なプロジェクトの形。 */
export interface DuckingProjectLike {
  ducking?: unknown;
  narrationTrack?: DuckingNarrationCueLike[] | null;
  sourceClips: DuckingClipLike[];
  trackMutes?: { narration?: boolean } | null;
}

/**
 * 下げる区間の一覧 (絶対秒・開始順・重なりなし)。
 *
 * - `trackMutes.narration` (トラック丸ごと除外) のときは空
 * - `cue.muted` の cue は鳴らないので含めない
 * - 実尺 (`sourceClips[].narrationDurationSec`) も鳴らす長さ (`durationSec`) も無い cue は終わりが決まらないので含めない
 * - 重なり、および **holdSec 以下の隙間**は 1 本に結合する (語りの切れ目のたびに BGM が
 *   上がり下がりして目立つのを防ぐ)
 */
export function buildDuckingIntervals(project: DuckingProjectLike): DuckingInterval[] {
  // `=== true` で判定する (backend の `is True` と揃える。truthy 判定だと 1 / '1' のような
  // 壊れた値で 3 実装の結果が食い違う)。
  if (project.trackMutes?.narration === true) return [];
  const holdSec = (normalizeDucking(project.ducking) ?? DUCKING_DEFAULTS).holdSec;
  const durationByClipId = new Map<string, number>();
  for (const clip of project.sourceClips ?? []) {
    // id が文字列でないクリップ (壊れた JSON) は突き合わせできないので飛ばす
    if (!clip || typeof clip.id !== 'string') continue;
    const duration = clip.narrationDurationSec;
    if (isFiniteNumber(duration) && duration > 0) durationByClipId.set(clip.id, duration);
  }

  const raw: DuckingInterval[] = [];
  for (const cue of project.narrationTrack ?? []) {
    if (!cue || cue.muted || typeof cue.clipId !== 'string') continue;
    const duration = narrationCuePlaySec(cue, durationByClipId.get(cue.clipId));
    if (duration === null) continue;
    const startSec = isFiniteNumber(cue.startSec) ? Math.max(0, cue.startSec) : 0;
    raw.push({ startSec, endSec: startSec + duration });
  }
  raw.sort((a, b) => a.startSec - b.startSec || a.endSec - b.endSec);

  const merged: DuckingInterval[] = [];
  for (const interval of raw) {
    const last = merged[merged.length - 1];
    if (last && interval.startSec <= last.endSec + holdSec + EPS) {
      last.endSec = Math.max(last.endSec, interval.endSec);
      continue;
    }
    merged.push({ ...interval });
  }
  return merged;
}

// ── エンベロープ ──────────────────────────────────────────────────────────────

/**
 * 絶対秒 t での BGM に掛けるゲイン (0〜1)。
 *
 * 区間 [s, e] 1 本に対して
 * ```
 * t <= s - attack        : 1
 * s - attack < t < s     : 1 + (target - 1) * (t - (s - attack)) / attack   // 線形に下がる
 * s <= t <= e            : target
 * e < t < e + release    : target + (1 - target) * (t - e) / release        // 線形に戻る
 * t >= e + release       : 1
 * ```
 * 区間が複数あるときは**各区間の値の最小**を採る (attack と release が重なる場所では
 * 下がっている方を優先する。区間は holdSec で結合済みなので通常は隣の区間としか重ならない)。
 *
 * 区間の開始が動画の先頭より前にあっても構わない (t=0 の時点で途中まで下がった値になる)。
 */
export function duckingGainAt(
  tSec: number,
  intervals: DuckingInterval[],
  params: Pick<DuckingSettings, 'targetDb' | 'attackSec' | 'releaseSec'>,
): number {
  const target = duckingTargetGain(params.targetDb);
  if (!(target < 1) || intervals.length === 0) return 1;
  const attack = Math.max(EPS, params.attackSec);
  const release = Math.max(EPS, params.releaseSec);

  let gain = 1;
  for (const { startSec, endSec } of intervals) {
    let g: number;
    if (tSec <= startSec - attack) g = 1;
    else if (tSec < startSec) g = 1 + (target - 1) * ((tSec - (startSec - attack)) / attack);
    else if (tSec <= endSec) g = target;
    else if (tSec < endSec + release) g = target + (1 - target) * ((tSec - endSec) / release);
    else g = 1;
    if (g < gain) gain = g;
  }
  return Math.min(1, Math.max(0, gain));
}

/**
 * エンベロープの折れ点 (絶対秒)。書き出し (Premiere の Audio Levels キーフレーム) が
 * 直線の端点だけを打てるようにするためのもので、区間ごとに
 * 「attack 開始 / 区間開始 / 区間終了 / release 終了」の 4 点を出す。
 * 昇順・重複なし。負の秒 (動画の先頭より前から下がり始める場合) もそのまま返す
 * (クランプは呼び出し側の座標系で行う)。
 */
export function duckingBreakpoints(
  intervals: DuckingInterval[],
  params: Pick<DuckingSettings, 'attackSec' | 'releaseSec'>,
): number[] {
  const attack = Math.max(0, params.attackSec);
  const release = Math.max(0, params.releaseSec);
  const points: number[] = [];
  for (const { startSec, endSec } of intervals) {
    points.push(startSec - attack, startSec, endSec, endSec + release);
  }
  points.sort((a, b) => a - b);
  const unique: number[] = [];
  for (const point of points) {
    if (unique.length === 0 || Math.abs(unique[unique.length - 1]! - point) > EPS) unique.push(point);
  }
  return unique;
}

/** 設定を 1 行で説明する (AI が読む要約・UI のツールチップ)。無効なら「無効」。 */
export function describeDucking(raw: unknown): string {
  const ducking = normalizeDucking(raw);
  if (!ducking || !ducking.enabled || ducking.targetDb >= 0) return '無効';
  return (
    `有効 (${ducking.targetDb} dB / attack ${ducking.attackSec}s / ` +
    `release ${ducking.releaseSec}s / hold ${ducking.holdSec}s)`
  );
}
